import React from 'react';

const Logo = () => (
  <div className="flex items-center gap-3">
    <svg width="32" height="32" viewBox="0 0 36 36" fill="none" overflow="visible" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 10 C9 4, 16 4, 21 10 C26 16, 33 16, 36 10" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
      <path d="M0 19 C6 12, 15 12, 21 19 C26 26, 32 26, 36 19" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
      <path d="M4 28 C9 22, 16 22, 21 28 C26 34, 33 34, 36 28" stroke="#22d3ee" strokeWidth="2.8" strokeLinecap="round"/>
    </svg>
    <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent tracking-tight">FlowSub</span>
  </div>
);

const features = [
  {
    title: 'On-chain subscriptions',
    description: 'Recurring USDC billing settled directly on the blockchain. No intermediaries, no chargebacks.',
    icon: '🔐',
  },
  {
    title: 'Live merchant dashboard',
    description: 'Real-time MRR, churn rate, and subscriber activity — pulled straight from contract events.',
    icon: '📊',
  },
  {
    title: 'Shareable payment links',
    description: 'Generate a hosted checkout link in seconds and share it anywhere.',
    icon: '🔗',
  },
  {
    title: 'Embeddable widget',
    description: 'Drop a subscription checkout directly into any website with a single snippet.',
    icon: '🧩',
  },
  {
    title: 'Automated billing',
    description: 'Payments process automatically each cycle. Merchants never have to chase invoices.',
    icon: '⚡',
  },
  {
    title: 'Instant refunds',
    description: 'Issue refunds on-chain in one click. Funds land in the customer\'s wallet immediately.',
    icon: '💸',
  },
];

export default function Landing({ onEnter }) {
  return (
    <div className="min-h-screen bg-[#080a0f] flex flex-col">

      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-cyan-500/[0.07] rounded-full blur-3xl" />
        <div className="absolute top-1/2 -right-20 w-[400px] h-[400px] bg-emerald-500/[0.05] rounded-full blur-3xl" />
      </div>

      {/* Nav */}
      <header className="border-b border-white/[0.07] bg-black/30 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <button
            onClick={onEnter}
            className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white/80 hover:text-white rounded-xl px-5 py-2 text-sm font-medium transition-all"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex-1 flex flex-col items-center justify-center text-center px-6 py-28">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 text-xs text-emerald-400 font-medium mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse"></span>
          Built on blockchain
        </div>

        <h1 className="text-5xl sm:text-6xl font-bold text-white tracking-tight leading-tight max-w-2xl">
          Subscription payments,<br />
          <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">on-chain.</span>
        </h1>

        <p className="mt-6 text-lg text-white/50 max-w-xl leading-relaxed">
          Your subscribers pay. Your platform takes a cut.{' '}
          <span className="text-white/80 font-medium">FlowSub runs recurring on-chain subscriptions at 1.5%</span>{' '}
          — no platform in the middle deciding what you earn.
        </p>

        <div className="mt-10">
          <button
            onClick={onEnter}
            className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold rounded-xl py-3 px-8 text-sm transition-all"
          >
            Get started
          </button>
        </div>
      </section>

      {/* Divider */}
      <div className="border-t border-white/[0.06]" />

      {/* Features */}
      <section className="relative max-w-5xl mx-auto px-6 py-20 w-full">
        <p className="text-[10px] uppercase tracking-widest text-white/35 font-medium mb-12 text-center">What's included</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-2xl overflow-hidden">
          {features.map((f) => (
            <div key={f.title} className="bg-[#080a0f] hover:bg-white/[0.03] px-7 py-8 transition-colors">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.07] text-xl mb-4">{f.icon}</div>
              <h3 className="text-sm font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-white/50 leading-relaxed">{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <Logo />
          <span className="text-xs text-white/30">Blockchain-native subscription infrastructure</span>
        </div>
      </footer>

    </div>
  );
}
